import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GETUsers, GetAdresses } from '../../services/users';
import { CreateCopperPipingEnquiry } from '../../services/copperPiping';
import { capitalizeWords } from '../../utils/helper';
import useSnackbar from '../../hooks/useSnackbar';
import {
  darkDialogPaperProps,
  darkFieldSx,
  darkItemCardSx,
  darkMenuPaperSx,
  darkTitleSx,
} from '../../components/common/darkFormStyles';

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

const AC_TYPE_OPTIONS = [
  'SPLIT_AC',
  'WINDOW_AC',
  'CASSETTE_AC',
  'VRV_VRF_AC',
  'DUCTED_AC',
  'TOWER_AC',
  'CHILLER_AC',
];

const initialAcType = { type: '', quantity: '1' };

const AddCopperPipingRequest = ({ open, onClose, onSaved }: Props) => {
  const [users, setUsers] = useState<any[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [addresses, setAddresses] = useState<any[]>([]);
  const [addressId, setAddressId] = useState('');
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [date, setDate] = useState('');
  const [slot, setSlot] = useState('FIRST_HALF');
  const [propertyType, setPropertyType] = useState('RESIDENTIAL');
  const [outdoorUnitLocation, setOutdoorUnitLocation] = useState('WALL_LOW');
  const [pipeLength, setPipeLength] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [acTypes, setAcTypes] = useState<any[]>([{ ...initialAcType }]);
  const [saving, setSaving] = useState(false);
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const userId = selectedUser?._id || '';

  useEffect(() => {
    if (!open) return;
    const delay = setTimeout(() => {
      setUsersLoading(true);
      GETUsers({ page: 1, limit: 50, searchQuery: userSearch.trim() })
        .then((response) => setUsers(response.data || []))
        .finally(() => setUsersLoading(false));
    }, 400);

    return () => clearTimeout(delay);
  }, [userSearch, open]);

  useEffect(() => {
    if (!userId) {
      setAddresses([]);
      setAddressId('');
      return;
    }
    setAddressesLoading(true);
    GetAdresses(userId)
      .then((response) => setAddresses(response.data || []))
      .finally(() => setAddressesLoading(false));
  }, [userId]);

  const updateAcType = (index: number, field: string, value: string) => {
    setAcTypes((prev) =>
      prev.map((item, itemIndex) => (itemIndex === index ? { ...item, [field]: value } : item)),
    );
  };

  const addAcType = () => setAcTypes((prev) => [...prev, { ...initialAcType }]);
  const removeAcType = (index: number) =>
    setAcTypes((prev) => prev.filter((_, itemIndex) => itemIndex !== index));

  const resetForm = () => {
    setSelectedUser(null);
    setUserSearch('');
    setAddressId('');
    setDate('');
    setSlot('FIRST_HALF');
    setPropertyType('RESIDENTIAL');
    setOutdoorUnitLocation('WALL_LOW');
    setPipeLength('');
    setAdditionalNotes('');
    setAcTypes([{ ...initialAcType }]);
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!userId || !addressId || !date || !slot || !pipeLength) {
      showSnackbar('Customer, address, date, slot and pipe length are required', 'error');
      return;
    }
    if (acTypes.some((item) => !item.type || !item.quantity || Number(item.quantity) < 1)) {
      showSnackbar('Select an AC type and quantity for every line', 'error');
      return;
    }

    try {
      setSaving(true);
      await CreateCopperPipingEnquiry({
        user_id: userId,
        addressId,
        date,
        slot,
        subType: 'COPPER_PIPING',
        copperPipingDetails: {
          propertyType,
          acTypes: acTypes.map((item) => ({
            type: item.type,
            quantity: Number(item.quantity) || 1,
          })),
          outdoorUnitLocation,
          pipeLength: Number(pipeLength),
          additionalNotes,
        },
      });
      showSnackbar('Copper piping request created', 'success');
      onSaved?.();
      resetForm();
      onClose();
    } catch (error) {
      showSnackbar(error instanceof Error ? error.message : 'Failed to create request', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={darkDialogPaperProps}>
      {SnackbarComponent}

      <DialogTitle sx={darkTitleSx}>
        Add Copper Piping Request
        <IconButton onClick={handleClose} sx={{ color: '#9e9e9e' }} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider sx={{ borderColor: '#2a2a2a' }} />

      <DialogContent sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Autocomplete
            options={users}
            value={selectedUser}
            loading={usersLoading}
            onChange={(_event, newValue) => setSelectedUser(newValue)}
            onInputChange={(_event, newInputValue) => setUserSearch(newInputValue)}
            getOptionLabel={(option) =>
              `${capitalizeWords(option.name || '') || option.phoneNumber} - ${option.phoneNumber}`
            }
            isOptionEqualToValue={(option, value) => option._id === value._id}
            sx={darkFieldSx}
            slotProps={{ paper: darkMenuPaperSx }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Customer"
                placeholder="Search by name or phone"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {usersLoading ? <CircularProgress size={16} sx={{ color: '#E31E25' }} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <FormControl fullWidth disabled={!userId || addressesLoading} sx={darkFieldSx}>
            <InputLabel>Address</InputLabel>
            <Select
              value={addressId}
              label="Address"
              onChange={(event) => setAddressId(event.target.value)}
              MenuProps={{ PaperProps: darkMenuPaperSx }}
            >
              {addresses.map((address) => (
                <MenuItem key={address._id} value={address._id}>
                  {[address.house, address.street, address.city, address.zipcode].filter(Boolean).join(', ')}
                </MenuItem>
              ))}
              {!addressesLoading && userId && addresses.length === 0 && (
                <MenuItem disabled value="">
                  No saved addresses for this customer
                </MenuItem>
              )}
            </Select>
          </FormControl>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              type="date"
              label="Inspection Date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                ...darkFieldSx,
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  filter: 'invert(1)',
                  cursor: 'pointer',
                },
              }}
            />
            <FormControl fullWidth sx={darkFieldSx}>
              <InputLabel>Slot</InputLabel>
              <Select
                value={slot}
                label="Slot"
                onChange={(event) => setSlot(event.target.value)}
                MenuProps={{ PaperProps: darkMenuPaperSx }}
              >
                <MenuItem value="FIRST_HALF">1st Half</MenuItem>
                <MenuItem value="SECOND_HALF">2nd Half</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <FormControl fullWidth sx={darkFieldSx}>
            <InputLabel>Place Type</InputLabel>
            <Select
              value={propertyType}
              label="Place Type"
              onChange={(event) => setPropertyType(event.target.value)}
              MenuProps={{ PaperProps: darkMenuPaperSx }}
            >
              <MenuItem value="RESIDENTIAL">Residential</MenuItem>
              <MenuItem value="COMMERCIAL">Commercial</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <FormControl fullWidth sx={darkFieldSx}>
              <InputLabel>Outdoor Unit Location</InputLabel>
              <Select
                value={outdoorUnitLocation}
                label="Outdoor Unit Location"
                onChange={(event) => setOutdoorUnitLocation(event.target.value)}
                MenuProps={{ PaperProps: darkMenuPaperSx }}
              >
                <MenuItem value="WALL_LOW">Wall (Low)</MenuItem>
                <MenuItem value="WALL_HIGH">Wall (High)</MenuItem>
                <MenuItem value="FLOOR">Floor</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Pipe Length (ft)"
              type="number"
              value={pipeLength}
              onChange={(event) => setPipeLength(event.target.value)}
              onWheel={(event) => (event.target as HTMLElement).blur()}
              sx={darkFieldSx}
            />
          </Box>

          <Typography sx={{ color: '#9e9e9e', fontSize: 13, fontWeight: 600 }}>AC Types</Typography>

          {acTypes.map((item, index) => (
            <Box key={index} sx={darkItemCardSx}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography sx={{ color: '#fff', fontWeight: 600 }}>Item {index + 1}</Typography>
                {acTypes.length > 1 && (
                  <IconButton size="small" onClick={() => removeAcType(index)} sx={{ color: '#9e9e9e' }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 2 }}>
                <FormControl fullWidth sx={darkFieldSx}>
                  <InputLabel>AC Type</InputLabel>
                  <Select
                    value={item.type}
                    label="AC Type"
                    onChange={(event) => updateAcType(index, 'type', event.target.value)}
                    MenuProps={{ PaperProps: darkMenuPaperSx }}
                  >
                    {AC_TYPE_OPTIONS.map((type) => (
                      <MenuItem key={type} value={type}>
                        {capitalizeWords(type.replaceAll('_', ' '))}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Quantity"
                  type="number"
                  value={item.quantity}
                  onChange={(event) => updateAcType(index, 'quantity', event.target.value)}
                  onWheel={(event) => (event.target as HTMLElement).blur()}
                  sx={darkFieldSx}
                />
              </Box>
            </Box>
          ))}

          <Button
            variant="outlined"
            onClick={addAcType}
            sx={{
              alignSelf: 'flex-start',
              color: '#E31E25',
              borderColor: '#E31E25',
              borderRadius: '10px',
              '&:hover': { borderColor: '#B71C1C', backgroundColor: 'rgba(227, 30, 37, 0.08)' },
            }}
          >
            + Add Another AC Type
          </Button>

          <TextField
            label="Additional Notes"
            value={additionalNotes}
            onChange={(event) => setAdditionalNotes(event.target.value)}
            multiline
            minRows={2}
            sx={darkFieldSx}
          />
        </Box>
      </DialogContent>

      <Divider sx={{ borderColor: '#2a2a2a' }} />

      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={handleClose} disabled={saving} sx={{ color: '#9e9e9e' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
          sx={{
            background: '#E31E25',
            color: '#fff',
            px: 4,
            py: 1.1,
            borderRadius: '10px',
            fontWeight: 600,
            '&:hover': { background: '#C62828' },
            '&.Mui-disabled': { background: '#7a2a2a', color: '#ddd' },
          }}
        >
          {saving ? 'Saving…' : 'Create Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCopperPipingRequest;
